import type { LazyQueryExecFunction } from "@apollo/client";
import { addMonths, endOfMonth, startOfMonth } from "date-fns";
import type {
  GetVenueReservationsRangeQueryInput,
  GetVenueReservationsRangeQueryResponse,
} from "./queries/GetVenueReservationsQuery";
import {
  createBlock,
  getBucketKey,
  isMonthWithinRange,
  mergeAdjacentBlocks,
  rangesOverlap,
} from "./reservationsBuckets";
import type {
  FetchReservationsOptions,
  ReservationSearch,
  ReservationsBucket,
} from "./reservationsTypes";

export type DefaultVariables = {
  venuePortalUserId: string;
  gracePeriodMs: number;
};

type MissingSegment = { from: Date; until: Date };

type FetchReservationsForSearchArgs = {
  userId?: string;
  from: Date | null;
  until: Date | null;
  search: ReservationSearch;
  options?: FetchReservationsOptions;
  defaultVariables: DefaultVariables;
  fetchReservationsQuery: LazyQueryExecFunction<
    GetVenueReservationsRangeQueryResponse,
    GetVenueReservationsRangeQueryInput
  >;
  getOrCreateBucket: (key: string) => ReservationsBucket;
  updateBucket: (
    key: string,
    updater: (bucket: ReservationsBucket) => ReservationsBucket,
  ) => ReservationsBucket;
  pastDefault: number;
  futureDefault: number;
};

/**
 * Computes the month segments not covered by existing blocks within a desired range.
 */
const buildMissingSegments = (
  desiredStart: Date,
  desiredEnd: Date,
  coverageBlocks: ReservationsBucket["blocks"],
) => {
  if (coverageBlocks.length === 0) {
    return [{ from: desiredStart, until: desiredEnd }];
  }
  const missingSegments: MissingSegment[] = [];
  let cursor = desiredStart;
  coverageBlocks.forEach((block) => {
    const blockStart =
      block.from.getTime() < desiredStart.getTime() ? desiredStart : block.from;
    const blockEnd =
      block.until.getTime() > desiredEnd.getTime() ? desiredEnd : block.until;
    if (blockStart.getTime() > cursor.getTime()) {
      missingSegments.push({
        from: cursor,
        until: startOfMonth(addMonths(blockStart, -1)),
      });
    }
    if (blockEnd.getTime() >= cursor.getTime()) {
      cursor = startOfMonth(addMonths(blockEnd, 1));
    }
  });
  if (cursor.getTime() <= desiredEnd.getTime()) {
    missingSegments.push({ from: cursor, until: desiredEnd });
  }
  return missingSegments;
};

/**
 * Fetches reservations for a search and updates the bucket with missing month blocks.
 */
export const fetchReservationsForSearch = async ({
  userId,
  from,
  until,
  search,
  options,
  defaultVariables,
  fetchReservationsQuery,
  getOrCreateBucket,
  updateBucket,
  pastDefault,
  futureDefault,
}: FetchReservationsForSearchArgs) => {
  if (!userId) return;
  const baseDate = options?.month ?? from ?? until ?? new Date();
  const useDefaultRange =
    !options || (options.past === undefined && options.future === undefined);
  const past = Math.max(
    0,
    Math.floor(options?.past ?? (useDefaultRange ? pastDefault : 0)),
  );
  const future = Math.max(
    0,
    Math.floor(options?.future ?? (useDefaultRange ? futureDefault : 0)),
  );
  const desiredStart = startOfMonth(addMonths(baseDate, -past));
  const desiredEnd = startOfMonth(addMonths(baseDate, future));
  const key = getBucketKey(search);
  const baseMonth = startOfMonth(baseDate);

  const bucketSnapshot = getOrCreateBucket(key);
  const baseBlock = bucketSnapshot.blocks.find((block) =>
    isMonthWithinRange(baseMonth, block.from, block.until),
  );

  const coverageBlocks = bucketSnapshot.blocks
    .filter((block) =>
      rangesOverlap(block.from, block.until, desiredStart, desiredEnd),
    )
    .sort((left, right) => left.from.getTime() - right.from.getTime());

  const missingSegments = buildMissingSegments(
    desiredStart,
    desiredEnd,
    coverageBlocks,
  );

  if (missingSegments.length === 0) {
    const fallbackBlock = baseBlock ?? coverageBlocks[0];
    if (fallbackBlock) {
      updateBucket(key, (currentBucket) => {
        currentBucket.activeBlockId = fallbackBlock.id;
        mergeAdjacentBlocks(currentBucket, fallbackBlock.id);
        return currentBucket;
      });
    }
    return;
  }

  const newBlocks: ReservationsBucket["blocks"] = [];
  for (const segment of missingSegments) {
    const result = await fetchReservationsQuery({
      variables: {
        ...defaultVariables,
        range: { from: segment.from, until: endOfMonth(segment.until) },
        ...search,
      },
    });
    const records =
      result.data?.venuePortalUser?.reservations?.venueReservations ?? [];
    newBlocks.push(createBlock(segment.from, segment.until, records));
  }

  updateBucket(key, (currentBucket) => {
    currentBucket.blocks = [...currentBucket.blocks, ...newBlocks];
    const activeBlock =
      currentBucket.blocks.find((block) =>
        isMonthWithinRange(baseMonth, block.from, block.until),
      ) ?? newBlocks[0];
    if (activeBlock) {
      currentBucket.activeBlockId = activeBlock.id;
      mergeAdjacentBlocks(currentBucket, activeBlock.id);
    }
    return currentBucket;
  });
};
